import { useCallback, useLayoutEffect, useState } from "react";
import type { RefObject } from "react";

export type DropdownPlacement = "bottom" | "top";

export type UseDropdownPlacementOptions = {
  /** The menu's natural max height in px. The hook never returns more than this. */
  preferredHeight: number;
  /** Gap between the trigger and the menu, matching the mt-1/mb-1 offset. */
  gap?: number;
  /** Keep this much clear of the viewport edge. */
  viewportPadding?: number;
};

export type DropdownPlacementResult = {
  placement: DropdownPlacement;
  maxHeight: number;
};

const DEFAULT_GAP = 4;
const DEFAULT_VIEWPORT_PADDING = 8;

/**
 * Decide whether a trigger-anchored menu should open below or above, and how tall it may be.
 *
 * A menu that only ever opens downward runs off the bottom of the viewport when its trigger
 * sits low on the page — and because an absolutely-positioned child still counts toward its
 * ancestor's scrollable overflow, it also stretches whatever scroll container it lives in.
 * Measuring the trigger lets the menu flip up instead, and clamps its height to the space
 * that is actually free on the chosen side.
 *
 * Measuring against the viewport (rather than the nearest clipping ancestor) is correct for
 * the current hosts: `page-container` is `h-[calc(100vh-64px)]` anchored at the top and the
 * slide-over panel is `fixed inset-y-0`, so both bottom out at the viewport bottom. A menu
 * placed inside a short inner scroller would need a boundary element instead.
 */
export function useDropdownPlacement(
  triggerRef: RefObject<HTMLElement>,
  isOpen: boolean,
  {
    preferredHeight,
    gap = DEFAULT_GAP,
    viewportPadding = DEFAULT_VIEWPORT_PADDING,
  }: UseDropdownPlacementOptions,
): DropdownPlacementResult {
  const [result, setResult] = useState<DropdownPlacementResult>({
    placement: "bottom",
    maxHeight: preferredHeight,
  });

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const { top, bottom } = trigger.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const spaceBelow = viewportHeight - bottom - gap - viewportPadding;
    const spaceAbove = top - gap - viewportPadding;

    // Stay below unless the menu genuinely does not fit there and above is roomier.
    const placement: DropdownPlacement =
      spaceBelow < preferredHeight && spaceAbove > spaceBelow
        ? "top"
        : "bottom";
    const available = placement === "top" ? spaceAbove : spaceBelow;
    // Clamping to preferredHeight matters: the caller applies this as an inline max-height,
    // which outranks its own max-h-* class and would otherwise let the menu grow past its cap.
    const maxHeight = Math.max(0, Math.min(preferredHeight, available));

    setResult((prev) =>
      prev.placement === placement && prev.maxHeight === maxHeight
        ? prev
        : { placement, maxHeight },
    );
  }, [triggerRef, preferredHeight, gap, viewportPadding]);

  // A layout effect, not an effect: this runs before paint, so the menu's first painted frame
  // is already on the correct side instead of flashing below the trigger and then flipping.
  useLayoutEffect(() => {
    if (!isOpen) return;

    measure();
    window.addEventListener("resize", measure);
    // Capture phase — a scroll inside an ancestor scroll container does not bubble to window.
    window.addEventListener("scroll", measure, true);

    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [isOpen, measure]);

  return result;
}
